import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import Grid from '@mui/material/Grid';
import InformationBox from '../../../../../components/other/informationBox';
import div from '@mui/material/Paper';
import PropTypes from 'prop-types';
import {emitChangedWait, obsStatuses, obsTypes} from '../obsUtils';
import ObsNumericStepper from './obsNumericStepper';
import renderAcceptSection from './renderAcceptSection';
import {obsStyles} from '../obsStyle';

function ObsGlucose(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [glucose, setGlucose] = React.useState(allowDefaultValue ? props.glucose : null);
    const [ketones, setKetones] = React.useState(allowDefaultValue ? props.ketones : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const setDefaults = () => {
        if (!allowDefaultValue) {
            setGlucose(null);
            setKetones(null);
        } else {
            setGlucose(4);
            setKetones(0);
        }
    };

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setGlucose(props.glucose);
            setKetones(props.ketones);
        }
    }, []);

    useEffect(() => {
        if ((_.isUndefined(glucose) || _.isNaN(glucose)) && allowDefaultValue) {
            if (_.isUndefined(props.glucose) || props.glucose == null || _.isNaN(props.glucose)) {
                setDefaults();
            } else {
                setGlucose(props.glucose);
            }
        }
    }, [props.glucose]);

    useEffect(() => {
        if ((_.isUndefined(ketones) || _.isNaN(ketones)) && allowDefaultValue) {
            if (_.isUndefined(props.ketones) || props.ketones == null || _.isNaN(props.ketones)) {
                setDefaults();
            } else {
                setKetones(props.ketones);
            }
        }
    }, [props.ketones]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (!_.isNull(glucose) && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);
        // const checkValueKetones = type !== obsTypes.neuro
        //     ? (glucose > 14 ? !_.isNull(ketones) : true)
        //     : true;

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [ketones, glucose, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    glucose,
                    ketones,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [status, glucose, ketones, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = 0;
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, glucose, ketones]);

    const onGlucoseChange = (value) => {
        setGlucose(value);
        if (value <= 14) {
            setKetones(!allowDefaultValue ? null : 0);
        }
        clearAccepted();
    };

    const onKetonesChange = (value) => {
        setKetones(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    const renderAlert = () => {
        if (!_.isNull(glucose) && glucose < 4) {
            return (
                <Grid item xs={12}>
                    <InformationBox type={'warning'}>
                        {t('Extremely low value')}
                    </InformationBox>
                </Grid>
            );
        } else if (!_.isNull(glucose) && glucose > 30) {
            return (
                <Grid item xs={12}>
                    <InformationBox type={'warning'}>
                        {t('Extremely high value')}
                    </InformationBox>
                </Grid>
            );
        } else if (!_.isNull(glucose) && glucose > 20) {
            return (
                <Grid item xs={12}>
                    <InformationBox type={'warning'}>
                        {t('Unusually high value')}
                    </InformationBox>
                </Grid>
            );
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12}>
                        <ObsNumericStepper
                            value={glucose}
                            minValue={0}
                            maxValue={100}
                            label={t('mmol/L')}
                            precision={1}
                            step={0.1}
                            onChange={onGlucoseChange}
                        />
                    </Grid>

                    {renderAlert()}

                    {type === obsTypes.neuro && glucose >= 14 && (
                        <Grid item xs={12}>
                            <InformationBox type={'tip'}>
                                {t('Numerical value entry is above 14, please record Ketones in standard observations')}
                            </InformationBox>
                        </Grid>
                    )}

                    {type !== obsTypes.neuro && (glucose >= 14 || config?.observations?.alwaysAvailableKetone) && (
                        <Grid item xs={12}>
                            {glucose >= 14 &&
                                <InformationBox type="tip">
                                    {t('Numerical value entry is above 14, please record Ketones')}
                                </InformationBox>
                            }

                            <div className={classes.obsLabel}>
                                {t('Ketones')}
                            </div>

                            <ObsNumericStepper
                                value={ketones}
                                minValue={0}
                                maxValue={100}
                                label={t('mmol/L')}
                                precision={1}
                                step={0.1}
                                onChange={onKetonesChange}
                            />
                        </Grid>
                    )}

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: true,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(glucose),
                        showStatusNR: type === obsTypes.pews,
                        hideStatuses: type === obsTypes.neuro,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsGlucose.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    glucose: PropTypes.number,
    ketones: PropTypes.number,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsGlucose.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    glucose: 4,
    ketones: 0,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsGlucose);